import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getList } from "../common/BackendProvider.jsx";
import { toast, ToastContainer } from "react-toastify";
import Button from '../layout/components/Button.jsx';
import { impactLevels, riskLevels, sdgAreas } from "./investmentProfiles/profileOptions";
import { normaliseProfile } from "./investmentProfiles/InvestmentProfileForm";

const findLabel = (items, id) => items.find((item) => item.id === id)?.title || "Not set";

const ProfileCard = ({ profile }) => {
    const normalised = normaliseProfile(profile);
    const selectedSdgs = sdgAreas.filter((sdg) => normalised.sdg_areas.includes(sdg.id));

    return (
        <Link
            to={`/investment_profiles/${profile.id}`}
            className="group block rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-600">Investment profile</div>
                    <h2 className="mt-2 truncate text-xl font-bold text-slate-950 group-hover:text-emerald-700">{normalised.name}</h2>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">
                        {normalised.description || "No narrative description has been added yet."}
                    </p>
                </div>
                <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-emerald-700">
                    {selectedSdgs.length} SDGs
                </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Risk</div>
                    <div className="mt-1 text-sm font-bold text-slate-950">{findLabel(riskLevels, normalised.risk_level)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Impact level</div>
                    <div className="mt-1 text-sm font-bold text-slate-950">{findLabel(impactLevels, normalised.impact_level)}</div>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                {normalised.impact_types.slice(0, 4).map((type) => (
                    <span key={type} className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                        {type}
                    </span>
                ))}
            </div>

            <div className="mt-5 grid grid-cols-6 gap-2 sm:grid-cols-8">
                {selectedSdgs.slice(0, 8).map((sdg) => (
                    <div
                        key={sdg.id}
                        title={sdg.title}
                        className={`flex h-8 items-center justify-center rounded-md text-xs font-bold text-white ${sdg.color}`}
                    >
                        {sdg.id}
                    </div>
                ))}
                {selectedSdgs.length === 0 ? (
                    <div className="col-span-full rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-400">
                        No SDG areas selected
                    </div>
                ) : null}
            </div>
        </Link>
    );
};

const SummaryTile = ({ label, value, detail }) => (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
        <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
        <div className="mt-1 text-sm font-semibold text-slate-500">{detail}</div>
    </div>
);

export const InvestmentProfiles = () => {
    const [investmentProfiles, setInvestmentProfiles] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadInvestmentProfiles = () => {
        setLoading(true);
        getList("investment-profiles", { pagination: { page: 1, perPage: 100 } }).then((response) => {
            setInvestmentProfiles(response.data.profiles || []);
            setLoading(false);
        }).catch(() => {
            toast.error("Error while loading investment profiles", {});
            setLoading(false);
        });
    };

    useEffect(() => {
        loadInvestmentProfiles();
    }, []);

    const stats = useMemo(() => {
        const profiles = investmentProfiles.map(normaliseProfile);
        const sdgCount = new Set(profiles.flatMap((profile) => profile.sdg_areas)).size;
        const impactTypes = new Set(profiles.flatMap((profile) => profile.impact_types)).size;

        return { sdgCount, impactTypes };
    }, [investmentProfiles]);

    return (
        <div className="space-y-5 p-4 lg:p-8">
            <ToastContainer />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-600">Investor mandates</div>
                    <h1 className="mt-2 text-3xl font-bold text-slate-950">Investment Profiles</h1>
                    <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
                        Turn investor preferences into visual mandates across risk, impact intensity, impact themes, and the 17 SDG areas.
                    </p>
                </div>
                <Link to="/investment_profiles/create">
                    <Button variant="primary">+ Add Investment Profile</Button>
                </Link>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
                <SummaryTile label="Profiles" value={investmentProfiles.length} detail="active mandates" />
                <SummaryTile label="SDG Areas" value={stats.sdgCount} detail="covered by profiles" />
                <SummaryTile label="Impact Themes" value={stats.impactTypes} detail="selected priorities" />
            </div>

            {loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-8 text-sm font-semibold text-slate-500 shadow-sm">
                    Loading investment profiles...
                </div>
            ) : (
                <div className="grid gap-4 xl:grid-cols-2">
                    {investmentProfiles.map((profile) => (
                        <ProfileCard key={profile.id} profile={profile} />
                    ))}
                </div>
            )}

            {!loading && investmentProfiles.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm">
                    <div className="text-lg font-bold text-slate-950">No investment profiles yet</div>
                    <div className="mt-2 text-sm font-medium text-slate-500">Create your first visual investor mandate to begin matching companies.</div>
                </div>
            ) : null}
        </div>
    );
};
